import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {UniqueIdentifier} from '@dnd-kit/core';
import {AccordionDocumentProps} from '../../../../entity/props/document/accordion-document-props';
import {Section} from '../../../../entity/resource/section';
import {useService} from '../../../../infrastructure/service/service';
import {PageOption} from '../../../../entity/enums/page-option';
import {Handle} from '../widget/Handle';
import 'react-loading-skeleton/dist/skeleton.css';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Box, Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import {Document} from '../../../../entity/resource/document';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ConfirmDialog from '../widget/ConfirmDialog';
import {debounce} from 'lodash';
import PublishIcon from '@mui/icons-material/Publish';
import {PublishDocumentDto} from '../../../../entity/dto/publish-document-dto';
import {isValidExtension} from '../../../../shared/document/validator';
import DownloadIcon from '@mui/icons-material/Download';
import InvoiceDialog from '../title/InvoiceDialog';
import {InvoiceRequest} from '../../../../entity/model/invoice-request';
import {OrderData} from '../../../../entity/props/title/invoice-props';
import { TitleSummaryRequest } from '../../../../entity/model/title-summary-request';
import { LineListRequest } from '../../../../entity/model/line-list-request';
import { ManageMode } from '../../../../entity/enums/manage-mode';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import '../../../../assets/css/style.css';
import '../../../../assets/css/document/accordion-document.css';

const AccordionDocument = (props: AccordionDocumentProps): JSX.Element => {
  const {sectionService, documentService, titleService, userService, titleTemplateService, invoiceRequestService, titleSummaryRequestService, lineListRequestService} = useService();

  const [deleteId, setDeleteId] = useState<string>();
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [inputVisibility, setInputVisibility] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState('');
  const [loadingRenameBtn, setLoadingRenameBtn] = useState<boolean>(false);
  const [loadingConfirmBtn, setLoadingConfirmBtn] = useState<boolean>(false);
  const [deleteCount, setDeleteCount] = useState<number>(0);
  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const [selectedDocument, setSelectedDocument] = useState<Document>();
  const [loading, setLoading] = useState<boolean>(false);
  const [canPublish, setCanPublish] = useState<boolean>(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const [openInvoiceEditDialog, setOpenInvoiceEditDialog] = useState<boolean>(false);
  const [invoiceTemplate, setInvoiceTemplate] = useState<InvoiceRequest>();
  const [titleSummaryRequest, setTitleSummaryRequest] = useState<TitleSummaryRequest>();
  const [lineListRequest, setLineListRequest] = useState<LineListRequest>();

  const textRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const title = titleService.getTitle();

  const orderData: OrderData = {
    orderNumber: props.project?.orderNumber || '',
    propertyAddress: title?.address || '',
    parcelNumber: title?.tmsParcelNumbers[0] || '',
    clientName: props.project?.client || '',
    orderDate: props.project?.dueDate || new Date().toISOString().split('T')[0]
  };

  useEffect(() => {
    const init = async (): Promise<void> => {
      document.documentElement.style.setProperty('--wrapper-height', '60px');

      if (props.document)
        setInputValue(props.document.name);

      const user = await userService.getCurrentUser();
      if (user && props.project && title && title.titleTemplateID) {
        const titleTemplate = await titleTemplateService.getTitleTemplateById(user?.organizationID ?? '', title?.titleTemplateID, false);
        setCanPublish(titleTemplate !== undefined && titleTemplate.createdByOrganizationID === user.organizationID);
      } else
        setCanPublish(false);

      setInvoiceTemplate(invoiceRequestService.getInvoiceTemplateByDocumentId(props.document?.id ?? ''));
      setTitleSummaryRequest(titleSummaryRequestService.getTitleSummaryRequest(props.document?.id ?? ''));
      setLineListRequest(lineListRequestService.getLineListRequest(props.document?.id ?? ''));
    }

    init();
  }, [])

  useEffect(() => {
    if (openConfirmDialog) {
      if (sectionService.getSelectedIds().length > 0)
        setDeleteCount(sectionService.getSelectedIds().length);
      else
        setDeleteCount(sectionService.getSelectedIds().length + 1);
    }
  }, [sectionService.getSelectedIds(), openConfirmDialog])

  useEffect(() => {
    const checkOverflow = (): void => {
      if (textRef.current && containerRef.current)
        setIsOverflow(textRef.current.scrollWidth > containerRef.current.clientWidth);
    };

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (containerRef.current)
      resizeObserver.observe(containerRef.current);

    checkOverflow();

    return (): void => {
      resizeObserver.disconnect();
    }
  }, [props.document?.name]);

  const getSectionByDocumentId = (id: UniqueIdentifier | string): Section | undefined => {
    return props.sections.find((section: Section): boolean => section.documents.some((document: Document): boolean => document.id === id));
  }

  const handleDeleteDocuments = async (event: React.MouseEvent<HTMLLIElement>): Promise<void> => {
    event.stopPropagation();
    handleMenuClose();

    if (selectedDocument) {
      setOpenConfirmDialog(true);
      setDeleteId(selectedDocument.id);
      sectionService.setViewDocument(true);
    }
  }

  const deleteCancelDocuments = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    event.stopPropagation();
    setOpenConfirmDialog(false);
    sectionService.setSelectedIds([]);
    sectionService.setViewDocument(false);
  }

  const deleteDocuments = useCallback(
    debounce(async (): Promise<void> => {
      setLoadingConfirmBtn(true);
      const section = getSectionByDocumentId(deleteId ?? '');

      if (sectionService.getSelectedIds().length > 0) {
        for (const [index, id] of sectionService.getSelectedIds().entries()) {
          await documentService.deleteDocument(
            props.project? props.project.createdByOrganizationID : titleTemplateService.getTitleTemplate()?.createdByOrganizationID ?? '',
            section?.id ?? '',
            id,
            index === sectionService.getSelectedIds().length - 1,
            props.titleId === '' ? undefined : props.titleId,
            !props.project ? titleTemplateService.getTitleTemplate()?.id ?? '' : undefined);
        }

        let sectionalContainer;
        if (props.project && props.titleId !== '') {
          const title = await titleService.getTitleById(props.project?.createdByOrganizationID ?? '', props.titleId);
          sectionalContainer = title?.sections.find((sec: Section): boolean => sec.id === section?.id);
        }

        let titleTemplate = titleTemplateService.getTitleTemplate();
        if (!props.project && titleTemplate) {
          titleTemplate = await titleTemplateService.getTitleTemplateById(titleTemplate.createdByOrganizationID, titleTemplate.id, true);
          sectionalContainer = titleTemplate?.sections.find((sec: Section): boolean => sec.id === section?.id);
        }

        props.setItems({
          ...props.items,
          [sectionalContainer?.id ?? '']: sectionalContainer?.documents?.map((doc: Document): string => doc.id) || []
        });
      } else if (deleteId && section) {
        await documentService.deleteDocument(
          props.project? props.project.createdByOrganizationID : titleTemplateService.getTitleTemplate()?.createdByOrganizationID ?? '',
          section.id,
          deleteId,
          true,
          props.titleId === '' ? undefined : props.titleId,
          !props.project ? titleTemplateService.getTitleTemplate()?.id ?? '' : undefined);

        let sectionalContainer;
        if (props.project && props.titleId !== '') {
          const title = await titleService.getTitleById(props.project?.createdByOrganizationID ?? '', props.titleId);
          sectionalContainer = title?.sections.find((sec: Section): boolean => sec.id === section?.id);
        }

        let titleTemplate = titleTemplateService.getTitleTemplate();
        if (!props.project && titleTemplate) {
          titleTemplate = await titleTemplateService.getTitleTemplateById(titleTemplate.createdByOrganizationID, titleTemplate.id, true);
          sectionalContainer = titleTemplate?.sections.find((sec: Section): boolean => sec.id === section?.id);
        }

        props.setItems({
          ...props.items,
          [sectionalContainer?.id ?? '']: sectionalContainer?.documents?.map((doc: Document): string => doc.id) || []
        });
      }

      sectionService.setViewDocument(false);
      setLoadingConfirmBtn(false);
      setOpenConfirmDialog(false);
      sectionService.setSelectedIds([]);
    }, 500),
    [deleteId, props.project, props.titleId, props.items]
  );

  const handleRenameDocument = (event: React.MouseEvent<HTMLLIElement, MouseEvent>): void => {
    event.stopPropagation();
    handleMenuClose();
    setInputVisibility(true);
  }

  const renameDocument = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string): Promise<void> => {
    event.stopPropagation();
    setLoadingRenameBtn(true);

    const section = getSectionByDocumentId(id);
    if (section) {
      const template = titleTemplateService.getTitleTemplate();
      let sectionalContainer;

      if (props.project) {
        await documentService.updateDocument(props.project.createdByOrganizationID, section.id, id, inputValue, props.titleId);

        const title = await titleService.getTitleById(props.project.createdByOrganizationID, props.titleId);
        sectionalContainer = title?.sections?.find((sec: Section): boolean => sec.id === section.id);
      } else if (template){
        await documentService.updateDocument(template.createdByOrganizationID, section.id, id, inputValue, undefined, template.id);

        const updatedTemplate = await titleTemplateService.getTitleTemplateById(template.createdByOrganizationID, template.id, true);
        sectionalContainer = updatedTemplate?.sections.find((sec: Section): boolean => sec.id === section.id);
      }

      props.items[sectionalContainer?.id ?? ''] = sectionalContainer?.documents?.map((doc: Document): string => doc.id) || [];
      props.setItems(props.items);

      setLoadingRenameBtn(false);
      setInputVisibility(false);
    }
  }

  const cancelRenameDocument = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    event.stopPropagation();
    setInputVisibility(false);
  }

  const handlePublishDocument = async (event: React.MouseEvent<HTMLLIElement, MouseEvent>): Promise<void> => {
    event.stopPropagation();
    handleMenuClose();
    setLoading(true);

    const publishDocumentDto: PublishDocumentDto = {
      sectionID: getSectionByDocumentId(props.document?.id ?? '')?.id ?? '',
      documentID: props.document?.id ?? '',
      isProjectTitle: true
    };
    await documentService.publishDocument(props.project?.createdByOrganizationID ?? '', title?.titleTemplateID ?? '', title?.id ?? '', publishDocumentDto);

    setLoading(false);
  }

  const handleViewDocument = async (): Promise<void> => {
    handleMenuClose();
    await viewDocument();
  }

  const viewDocument = async (): Promise<void> => {
    if (documentService.getDocument() && props.document?.id !== documentService.getDocument()?.id) {
      sectionService.setViewDocument(false);
      titleService.setPageOption(PageOption.DOCUMENTS);
      documentService.setDocument(undefined);
    }

    sectionService.setViewDocument(!sectionService.getViewDocument());

    if (sectionService.getViewDocument()) {
      documentService.setDocument(props.document);
      documentService.setDocumentSection(getSectionByDocumentId(props.document?.id ?? ''));
      titleService.setPageOption(PageOption.VIEW_PDF);
    } else {
      titleService.setPageOption(PageOption.DOCUMENTS);
      documentService.setDocument(undefined);
    }
  }

  const handleSplitDocument = (): void => {
    handleMenuClose();
    documentService.setDocument(props.document);
    titleService.setPageOption(PageOption.SPLIT_PDF);
  }

  const handleDownloadDocument = async (): Promise<void> => {
    handleMenuClose();
    documentService.downloadDocument(selectedDocument?.url ?? '', selectedDocument?.name ?? '');
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, document?: Document): void => {
    event.stopPropagation();
    setMenuElement(event.currentTarget);
    setSelectedDocument(document);
  }

  const handleMenuClose = (): void => {
    setMenuElement(null);
  }

  const handleEditDocument = (): void => {
    handleMenuClose();

    if (invoiceTemplate)
      setOpenInvoiceEditDialog(true);
    else if (titleSummaryRequest && title && props.project) {
      const documentSection = getSectionByDocumentId(props.document?.id ?? '');
      const sectionId = documentSection && documentSection.name !== 'Unclassified' ? documentSection.id : undefined;
      titleSummaryRequestService.setCurrentProps({title: title, project: props.project, reportData: titleSummaryRequest, mode: ManageMode.UPDATE, sectionId: sectionId});
      window.open(`/organization/${title.createdByOrganizationID}/title/${title.id}/title-summary-report`, '_blank');
    }
    else if (lineListRequest && title && props.project) {
      const documentSection = getSectionByDocumentId(props.document?.id ?? '');
      const sectionId = documentSection && documentSection.name !== 'Unclassified' ? documentSection.id : undefined;
      lineListRequestService.setCurrentProps({title: title, project: props.project, reportData: lineListRequest, mode: ManageMode.UPDATE, sectionId: sectionId});
      window.open(`/organization/${title.createdByOrganizationID}/title/${title.id}/line-list-report`, '_blank');
    }
  }

  const handleOpenDocument = (): void => {
    handleMenuClose();
    if (props.document?.url) {
      window.open(props.document.url, '_blank');
    }
  }

  const renderDocumentMenu = (): JSX.Element => {
    return (
      <Menu elevation={0} anchorEl={menuElement} open={Boolean(menuElement)} className={'zitles-menu'} onClose={handleMenuClose}>
        {
          sectionService.getSelectedIds().length < 2 &&
            <Box>
              <MenuItem disableRipple className={'zitles-menu-item'} onClick={handleViewDocument}>
                <VisibilityIcon className={'zitles-icon'}/>
                <Typography className={'zitles-menu-item button'}>View</Typography>
              </MenuItem>
              <MenuItem disableRipple className={'zitles-menu-item'} onClick={handleOpenDocument}>
                <OpenInNewIcon className={'zitles-icon'}/>
                <Typography className={'zitles-menu-item button'}>Open</Typography>
              </MenuItem>
              {
                !props.actionDenied &&
                  <Box>
                    <MenuItem disableRipple className={'zitles-menu-item'} onClick={(event: React.MouseEvent<HTMLLIElement, MouseEvent>): void => handleRenameDocument(event)}>
                      <EditIcon className={'zitles-icon'} fontSize={'small'} sx={{marginLeft: '5px'}}/>
                      <Typography className={'zitles-menu-item button'}>Rename</Typography>
                    </MenuItem>
                    {
                      (invoiceTemplate || titleSummaryRequest || lineListRequest) && !props.actionDenied &&
                        <MenuItem disableRipple className={'zitles-menu-item'} onClick={handleEditDocument}>
                          <DescriptionIcon className={'zitles-icon'} fontSize={'small'}/>
                          <Typography className={'zitles-menu-item button'}>Edit</Typography>
                        </MenuItem>
                    }
                    {
                      props.project &&
                        <MenuItem disableRipple className={'zitles-menu-item'} onClick={handleSplitDocument}>
                          <VerticalSplitIcon className={'zitles-icon'}/>
                          <Typography className={'zitles-menu-item button'}>Split</Typography>
                        </MenuItem>
                    }
                    {
                      canPublish && props.project &&
                        <MenuItem disableRipple className={'zitles-menu-item'} onClick={(event: React.MouseEvent<HTMLLIElement, MouseEvent>): Promise<void> => handlePublishDocument(event)}>
                          <PublishIcon className={'zitles-icon'}/>
                          <Typography className={'zitles-menu-item button'}>Publish</Typography>
                        </MenuItem>
                    }
                    <MenuItem disableRipple className={'zitles-menu-item'} onClick={handleDownloadDocument}>
                      <DownloadIcon className={'zitles-icon'}/>
                      <Typography className={'zitles-menu-item button'}>Download</Typography>
                    </MenuItem>
                    <Divider/>
                </Box>
              }
            </Box>
        }
        {
          !props.actionDenied &&
            <MenuItem disableRipple className={'zitles-menu-item'} onClick={(event: React.MouseEvent<HTMLLIElement, MouseEvent>): Promise<void> => handleDeleteDocuments(event)}>
              <DeleteIcon className={'zitles-icon'}/>
              <Typography className={'zitles-menu-item button'}>Delete</Typography>
            </MenuItem>
        }
      </Menu>
    );
  }

  return (
    <>
      <List id={'document-component'}>
        <ListItem className={'accordion-document'}>
          {
            props.handle &&
              <Handle id={'document-drag-btn'} disabled={props.actionDenied || !isValidExtension(props.document?.name)} {...props.handleProps} {...props.listeners} />
          }
          <PictureAsPdfIcon className='zitles-icon' cursor={'pointer'} onClick={viewDocument}/>
          {
            inputVisibility
              ? <Box className={'rename-document-container'}>
                  <TextField
                    className={'document-input'}
                    value={inputValue}
                    placeholder='File Name'
                    onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => event.stopPropagation()}
                    onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => setInputValue(event.target.value)}/>
                  <Button
                    disabled={loadingRenameBtn}
                    className={`zitles-button submit ${loadingRenameBtn ? 'disable' : ''}`}
                    onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> => renameDocument(event, props.document?.id ?? '')}
                    startIcon={loadingRenameBtn ? <CircularProgress size={14} color='inherit'/> : null}>
                    {
                      loadingRenameBtn ? 'Saving' : 'Save'
                    }
                  </Button>
                  <Button
                    disableTouchRipple
                    onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => cancelRenameDocument(event)}
                    className={'zitles-button cancel rename-cancel'}>
                    Cancel
                  </Button>
                </Box>
              : <Box ref={containerRef} className={'document-name-container'}>
                {
                  isOverflow
                    ? <Tooltip title={props.document?.name} arrow>
                        <Typography ref={textRef} className='accordion-document-name ellipsis'>{ props.document?.name }</Typography>
                      </Tooltip>
                    : <Typography ref={textRef} className='accordion-document-name'>{ props.document?.name }</Typography>
                }
              </Box>
          }
          <Box className={'accordion-action-container'}>
            {
              (documentService.getMovingDocumentId() === props.document?.id && documentService.getDocumentMoving()) || loading
                ? <CircularProgress color={'inherit'} size={14} />
                : !inputVisibility &&
                    <Box>
                      <IconButton
                        className={'zitles-menu-trigger'}
                        color="inherit"
                        disableRipple
                        disableFocusRipple
                        onClick={(event: React.MouseEvent<HTMLElement>): void => handleMenuOpen(event, props.document)}>
                        <MoreHorizIcon/>
                      </IconButton>
                      {renderDocumentMenu()}
                    </Box>
            }
          </Box>
        </ListItem>
      </List>
      {
        openConfirmDialog &&
          <ConfirmDialog
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            title={`Delete Document${deleteCount > 1 ? 's' : ''}`}
            message={`Are you sure you want to delete ${deleteCount} document${deleteCount > 1 ? 's' : ''}?`}
            handleSubmit={deleteDocuments}
            loading={loadingConfirmBtn}
            handleCancel={deleteCancelDocuments}/>
      }
      {
        openInvoiceEditDialog && props.project && title &&
          <InvoiceDialog
            orderData={orderData}
            project={props.project}
            title={title}
            isOpen={openInvoiceEditDialog}
            invoiceTemplate={invoiceTemplate}
            onClose={() => setOpenInvoiceEditDialog(false)}/>
      }
    </>
  );
}

export default observer(AccordionDocument);
